import { db } from "config/firebase.config";
import { doc, collection, onSnapshot, addDoc, Timestamp, deleteDoc, updateDoc, setDoc, getDoc, query, where, getDocs } from "firebase/firestore";
import { deleteUserFromAuth, formatDate, sendLeaveRequestEmail, sendLeaveRequestEmailByManager, sendWelcomeEmail, calculateWorkingDays, getNSWHolidays } from "utils/utils";

export const listenToUser = (userId, callback) => {
    if (!userId) return () => { };

    const unsub = onSnapshot(doc(db, "users", userId), (docSnap) => {
        if (docSnap.exists()) {
            callback({ id: docSnap.id, ...docSnap.data() });
        } else {
            callback(null);
        }
    });

    return unsub;
};

export const listenToAllUsers = (callback) => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
        callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
};

export const addUser = async (newUser) => {
    if (!newUser?.email || !newUser?.name || !newUser?.id) {
        throw new Error("Missing required user data (id, name, email)");
    }

    try {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", newUser.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            console.log(`⚠️ User with email ${newUser.email} already exists.`);
            throw new Error("User with this email already exists.");
        }

        const response = await sendWelcomeEmail(
            newUser.email,
            newUser.name,
            newUser.id
        );

        const user = response?.data?.user;

        if (!user) {
            throw new Error("Failed to send welcome email or user data is missing");
        }
        const uid = user?.uid || newUser.id;

        const initials =
            newUser.name.match(/\b\w/g)?.join('').slice(0, 2).toUpperCase() || "";

        const userRef = doc(db, "users", uid);
        await setDoc(userRef, {
            ...newUser,
            initials,
            isActive: true,
        });

        const savedUserSnap = await getDoc(userRef);
        if (savedUserSnap.exists()) {
            console.log(`✅ User ${newUser.name} created successfully.`);
            return { id: savedUserSnap.id, ...savedUserSnap.data() };
        } else {
            throw new Error("User was not saved properly.");
        }

    } catch (error) {
        console.error("❌ Error adding user:", error);
        throw new Error(error.message || "Failed to add user");
    }
};

export const updateUser = async (userId, userData) => {
    try {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, userData);
        console.log(`✅ User ${userData.name || userId} updated successfully`);
    } catch (error) {
        console.error(`❌ Failed to update user ${userData.name || userId}:`, error);
        throw error;
    }
};

export const deleteUserById = async (userId) => {
    if (!userId) throw new Error("Missing user ID");

    try {
        await deleteDoc(doc(db, "users", userId));
        await deleteUserFromAuth(userId);

        console.log(`✅ User ${userId} deleted from Firestore and Auth.`);
    } catch (error) {
        console.error("❌ Error deleting user:", error);
        throw error;
    }
};

export const listenToAllLeaveRequests = (callback) => {
    const unsub = onSnapshot(collection(db, "leaveRequests"), (snap) => {
        callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
};

export const submitLeaveRequest = async (leaveData) => {
    try {
        if (!leaveData?.employeeId || !leaveData?.startDate || !leaveData?.endDate) {
            throw new Error("Missing required leave request data (employeeId, startDate, endDate)");
        }
        const { employeeEmail, managerId, leaveType, startDate, endDate, status } = leaveData;

        // Get employee details to check if they're a manager
        const employeeDoc = await getDoc(doc(db, "users", leaveData.employeeId));
        if (!employeeDoc.exists()) {
            throw new Error(`Employee with ID ${leaveData.employeeId} does not exist`);
        }
        const employeeData = employeeDoc.data();

        // If no manager assigned, check if employee is a manager for auto-approval
        if (!managerId) {
            if (employeeData.role === 'manager') {
                // Auto-approve for managers with no manager
                leaveData.status = 'approved';
                leaveData.managerEmail = employeeData.email;
                leaveData.managerName = employeeData.name;
            } else {
                throw new Error("No manager assigned for this employee");
            }
        } else {
            // Get manager details
            const managerDoc = await getDoc(doc(db, "users", managerId));
            if (!managerDoc.exists()) {
                throw new Error(`Manager with ID ${managerId} does not exist`);
            }
            leaveData.managerEmail = managerDoc.data().email;
            leaveData.managerName = managerDoc.data().name;
        }

        // Fetch public holidays and calculate working days excluding weekends and public holidays
        const publicHolidays = await getNSWHolidays();
        const totalDays = calculateWorkingDays(startDate, endDate, publicHolidays);

        // Send emails based on status
        if (leaveData.status === 'pending') {
            await sendLeaveRequestEmail(
                leaveData.managerEmail,
                leaveData.employeeName,
                leaveData.managerName,
                leaveType,
                startDate,
                endDate,
                totalDays,
                leaveData.comments
            );
        }

        if (leaveData.status === 'approved') {
            await sendLeaveRequestEmailByManager(
                employeeEmail,
                leaveData.employeeName,
                leaveData.managerName,
                leaveType,
                startDate,
                endDate,
                totalDays,
                leaveData.comments
            );
        }

        const { employeeEmail: _, ...leaveDataToSave } = leaveData;

        await addDoc(collection(db, "leaveRequests"), {
            ...leaveDataToSave,
            createdAt: Timestamp.now(),
        });
    } catch (error) {
        console.error("Error submitting leave request:", error);
        throw error;
    }
};

export const updateLeaveStatus = async (requestId, status) => {
    if (!requestId || !status) throw new Error("Missing request ID or status");

    try {
        const leaveRef = doc(db, "leaveRequests", requestId);
        await updateDoc(leaveRef, { status });

        console.log(`✅ Leave request ${requestId} updated to '${status}'`);
    } catch (error) {
        console.error("❌ Error updating leave status:", error);
        throw error;
    }
};

export const deleteLeaveRequest = async (requestId) => {
    if (!requestId) throw new Error("Missing request ID or status");

    try {
        const leaveRef = doc(db, "leaveRequests", requestId);
        await deleteDoc(leaveRef);

        console.log(`🗑️ Leave request ${requestId} deleted successfully`);
    } catch (error) {
        console.error("❌ Error updating leave status:", error);
        throw error;
    }
};

export const listenToAllTimesheetApprovals = (callback) => {
    const unsub = onSnapshot(collection(db, "timesheetApprovals"), (snap) => {
        callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
};

export const addTimesheet = async (employeeId, periodId) => {
    if (!employeeId || !periodId) {
        throw new Error("Missing required fields for timesheet approval");
    }

    const approvalId = `${employeeId}_${periodId}`;

    try {
        await setDoc(doc(db, "timesheetApprovals", approvalId), {
            employeeId,
            periodId,
            status: "pending",
        });

        console.log(`✅ Timesheet ${approvalId} added`);
    } catch (error) {
        console.error("❌ Error approving timesheet:", error);
        throw error;
    }
};

export const deleteTimesheet = async (employeeId, periodId) => {
    if (!employeeId || !periodId) {
        throw new Error("Missing required fields for timesheet deletion");
    }

    const approvalId = `${employeeId}_${periodId}`;

    try {
        await deleteDoc(doc(db, "timesheetApprovals", approvalId));
        console.log(`🗑️ Timesheet ${approvalId} deleted`);
    } catch (error) {
        console.error("❌ Error deleting timesheet:", error);
        throw error;
    }
};

export const approveTimesheet = async (employeeId, periodId, approvedByUid) => {
    if (!employeeId || !periodId || !approvedByUid) {
        throw new Error("Missing required fields for timesheet approval");
    }

    const approvalId = `${employeeId}_${periodId}`;

    try {
        await setDoc(doc(db, "timesheetApprovals", approvalId), {
            employeeId,
            periodId,
            status: "approved",
            approvedBy: approvedByUid,
            approvedAt: new Date().toISOString(),
        }, { merge: true });

        console.log(`✅ Timesheet ${approvalId} approved by ${approvedByUid}`);
    } catch (error) {
        console.error("❌ Error approving timesheet:", error);
        throw error;
    }
};

export async function checkLeaveOverlap(employeeId, newStartDate, newEndDate) {
    const leaveRequestRef = collection(db, "leaveRequests");
    const q = query(
        leaveRequestRef,
        where("employeeId", "==", employeeId),
        where("status", "in", ["approved", "pending", "draft"])
    );
    const querySnapshot = await getDocs(q);

    const newStart = formatDate(newStartDate);
    const newEnd = formatDate(newEndDate);

    for (const doc of querySnapshot.docs) {
        const data = doc.data();
        const existingStart = formatDate(data.startDate);
        const existingEnd = formatDate(data.endDate);

        if (newStart <= existingEnd && existingStart <= newEnd) {
            return {
                overlap: true,
                message: `Leave overlaps with existing leave from ${data.startDate} to ${data.endDate}`,
            };
        }
    }

    return { overlap: false };
}